import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Loader, Zap, Plus, Check } from 'lucide-react';
import { getAuthHeaders } from '../../context/AuthContext';
import { BACKEND_URL } from '../../config/api';
import { showDetectionErrors } from '../../utils/detectionErrors';
import SentimentDialog from './SentimentDialog';

const SentimentSection = ({ sentiments, onSentimentsChange, imageUrl, disabled, onAddingStateChange }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [detecting, setDetecting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [pendingDetected, setPendingDetected] = useState([]);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const listEndRef = useRef(null);

  const hasImage = !!imageUrl;

  // Notify parent of adding state
  useEffect(() => {
    onAddingStateChange?.(isAdding);
  }, [isAdding, onAddingStateChange]);

  // Focus input when adding
  useEffect(() => {
    if (isAdding) setTimeout(() => inputRef.current?.focus(), 50);
  }, [isAdding]);

  // Click outside handler
  useEffect(() => {
    if (!isAdding) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsAdding(false);
        setNewValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAdding]);

  const handleConfirmAdd = () => {
    const val = newValue.trim();
    if (val && !sentiments.includes(val)) {
      onSentimentsChange([...sentiments, val]);
      setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
    setNewValue('');
    setIsAdding(false);
  };

  const handleRemove = (idx) => {
    onSentimentsChange(sentiments.filter((_, i) => i !== idx));
  };

  // Scan Image → call API → show SentimentDialog for confirmation
  const handleScanImage = async () => {
    if (!imageUrl) return;
    setDetecting(true);
    try {
      const fd = new FormData();
      fd.append('image_url', imageUrl);
      fd.append('detect_mode', 'sentiment');
      const resp = await fetch(`${BACKEND_URL}/api/stash/process-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: fd,
      });
      if (resp.ok) {
        const data = await resp.json();
        showDetectionErrors(data);
        if (data.sentiments?.length) {
          setPendingDetected(data.sentiments);
          setShowDialog(true);
        } else if (!data.detection_errors?.length) {
          alert('No text or sentiments detected in the image.');
        }
      }
    } catch (err) {
      console.error('Scan image error:', err);
      alert('Failed to scan image for sentiments.');
    } finally {
      setDetecting(false);
    }
  };

  // Rescan handler for SentimentDialog
  const handleRescan = useCallback(async () => {
    if (!imageUrl) return [];
    try {
      const fd = new FormData();
      fd.append('image_url', imageUrl);
      fd.append('detect_mode', 'sentiment');
      const resp = await fetch(`${BACKEND_URL}/api/stash/process-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: fd,
      });
      if (resp.ok) {
        const data = await resp.json();
        showDetectionErrors(data);
        return data.sentiments || [];
      }
    } catch (err) {
      console.error('Rescan sentiments error:', err);
    }
    return [];
  }, [imageUrl]);

  // Dialog confirm → merge into sentiments
  const handleConfirmDialog = () => {
    const existing = new Set(sentiments.map(s => s.toLowerCase()));
    const toAdd = pendingDetected.filter(s => !existing.has(s.toLowerCase()));
    if (toAdd.length > 0) {
      onSentimentsChange([...sentiments, ...toAdd]);
      setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
    setPendingDetected([]);
    setShowDialog(false);
  };

  const handleCancelDialog = () => {
    setPendingDetected([]);
    setShowDialog(false);
  };

  return (
    <div>
      {/* Scan Image → SentimentDialog for confirmation */}
      {showDialog && (
        <SentimentDialog
          pendingSentiments={pendingDetected}
          setPendingSentiments={setPendingDetected}
          onConfirm={handleConfirmDialog}
          onCancel={handleCancelDialog}
          onRescan={imageUrl ? handleRescan : undefined}
        />
      )}

      {/* Header: label + buttons */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <label className="text-sm font-medium text-charcoal">Sentiments</label>
          <p className="text-xs text-gray-soft">{sentiments.length} added</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasImage && (
            <button
              type="button"
              onClick={handleScanImage}
              disabled={detecting || isAdding || disabled}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-sage text-sage rounded-lg hover:bg-sage hover:text-white transition-colors disabled:opacity-50 whitespace-nowrap"
              data-testid="detect-sentiments-btn"
            >
              {detecting ? <Loader size={14} className="animate-spin" /> : <Zap size={14} />}
              {detecting ? 'Scanning...' : 'Scan Image'}
            </button>
          )}
          <button
            type="button"
            onClick={() => { setIsAdding(true); setNewValue(''); }}
            disabled={isAdding || disabled}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-sage text-sage rounded-lg hover:bg-sage hover:text-white transition-colors disabled:opacity-50 whitespace-nowrap"
            data-testid="add-sentiment-btn"
          >
            <Plus size={14} />
            Add
          </button>
        </div>
      </div>

      {/* Inline add text box — above the sentiments list */}
      {isAdding && (
        <div ref={wrapperRef} className="flex items-center gap-2 mb-3" data-testid="inline-sentiment-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleConfirmAdd(); }
              if (e.key === 'Escape') { setIsAdding(false); setNewValue(''); }
            }}
            placeholder="Type a sentiment..."
            className="flex-1 px-3 py-2.5 border border-sage rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-sage/30"
            data-testid="new-sentiment-input"
          />
          <button
            type="button"
            onClick={handleConfirmAdd}
            disabled={!newValue.trim()}
            className="p-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="confirm-new-sentiment-btn"
          >
            <Check size={16} />
          </button>
        </div>
      )}

      {/* Sentiment pills */}
      {sentiments.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {sentiments.map((s, idx) => (
            <span
              key={`${s}-${idx}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sage-light text-charcoal rounded-full text-sm group"
              data-testid={`sentiment-tag-${idx}`}
            >
              {s}
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="text-gray-300 hover:text-red-500 transition-colors"
                data-testid={`remove-sentiment-${idx}`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          <div ref={listEndRef} />
        </div>
      ) : (
        <p className="text-sm text-gray-soft italic">No sentiments added yet.</p>
      )}
    </div>
  );
};

export default SentimentSection;
